/**
 * Générateur — Utiliser les nombres relatifs pour représenter des grandeurs
 * observables (température, temps, altitude, argent…), en particulier dans le
 * cadre de la résolution de problèmes.
 * Attendu ID : 705 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 1)
 *
 * Plan de cartes (validé — Ex.5 « Hauteurs et profondeurs » ÉCARTÉ, hors quota) :
 *
 *   Carte « traduire » — Ex.4 Traduire par un nombre relatif
 *     relier (phrase ↔ nombre relatif — zéro frappe de signe), texte (saisie,
 *     écriture imposée AVEC signe), qcm_unique (pas de choix « nu » sans
 *     signe : il serait aussi juste que « +v » → distracteurs = signe opposé
 *     et erreurs de grandeur). Pool = TOUS les contextes du programme :
 *     altitude, profondeur, temps (av. J.-C.), argent, étage, apnée, soldes,
 *     température, gain.
 *
 *   Carte « ascenseur » — Ex.1 L'ascenseur (adapté : ordonner)
 *     ordonner (les boutons du plus bas au plus haut — l'adaptation du doc,
 *     sans image de panneau) + texte_trou_drag (panneau à compléter).
 *     Plage tirée : 1-3 sous-sols, 4-6 étages.
 *
 *   Carte « variations » — Créé n°12 Variations de température
 *     texte_trou_drag + qcm_unique. La variation est LUE sur l'échelle
 *     (aucune règle d'addition des relatifs — attendu ultérieur) ; les cas
 *     « de a °C à b °C » traversent zéro ou non (rotation), automatisme B2.
 *
 * Politique : signe « - » = tiret clavier ; saisies signées → écriture
 * imposée en consigne ; drag pour tout ce qui porte l'enjeu du signe.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeOrdonner, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 705;

const L_TRADUIRE = 'Traduire par un nombre relatif';
const L_ASCENSEUR = 'L\'ascenseur';
const L_VARIATIONS = 'Variations de température';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signe = (d) => (d < 0 ? dix(d) : `+${dix(d)}`);

// ═════════ Carte 1 — Traduire par un nombre relatif (Ex.4) ═════════
// v en UNITÉS entières ; s = signe du nombre relatif attendu.

// (noms à initiale consonne uniquement — « de Agonac » casserait l'élision)
const LIEUX = ['Belvès', 'Vaunac', 'Trélissac', 'Nontron', 'Beynac', 'Sorges'];
const CONTEXTES = [
    { s: +1, v: () => randInt(10, 33) * 10, ph: (v) => `La hauteur de la tour de ${randomChoice(LIEUX)} est d'environ ${v} mètres.` },
    { s: -1, v: () => randInt(8, 40) * 10, ph: (v) => `La profondeur de la fosse marine de ${randomChoice(LIEUX)} est d'environ ${v} mètres.` },
    { s: +1, v: () => randInt(8, 36) * 50, ph: (v) => `Le village de ${randomChoice(LIEUX)} est situé à ${v} mètres d'altitude.` },
    { s: -1, v: () => randInt(42, 720), ph: (v) => `La bataille de ${randomChoice(LIEUX)} a eu lieu en ${v} avant Jésus-Christ.` },
    { s: -1, v: () => randInt(2, 30) * 10, ph: (v) => `Mon compte bancaire est débiteur de ${v} euros.` },
    { s: +1, v: () => randInt(3, 15), ph: (v) => `Le bureau du directeur est au ${v}e étage.` },
    { s: -1, v: () => randInt(61, 129), ph: (v) => `Le record de plongée en apnée du club est de ${v} mètres.` },
    { s: -1, v: () => randInt(2, 14) * 5, ph: (v) => `Cette veste était soldée à ${v} %.` },
    { s: -1, v: () => randInt(1, 15), ph: (v) => `La température est de ${v} °C au-dessous de zéro.` },
    { s: +1, v: () => randInt(5, 40), ph: (v) => `L'équipe a gagné ${v} points au classement.` },
];

/** Tire k contextes distincts avec des valeurs deux à deux distinctes. */
const tirerContextes = (k) => {
    const idx = shuffle(CONTEXTES.map((_, i) => i)).slice(0, k);
    const vus = new Set();
    return idx.map(i => {
        const ctx = CONTEXTES[i];
        let v = ctx.v();
        for (let essai = 0; essai < 30 && vus.has(v); essai++) v = ctx.v();
        vus.add(v);
        return { phrase: ctx.ph(v), relatif: ctx.s * v };
    });
};

const genTraduireRelier = () => {
    const ctxs = tirerContextes(3);
    const pairs = ctxs.map(c => ({ source: c.phrase, target: signe(c.relatif * 10) }));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque information au nombre relatif qui la traduit.</b></p>',
        opts(L_TRADUIRE));
    payload._meta.params = { relatifs: ctxs.map(c => c.relatif) };
    return payload;
};

const genTraduireSaisie = () => {
    const [ctx] = tirerContextes(1);
    const payload = makeSaisie(signe(ctx.relatif * 10),
        `<p>${ctx.phrase} <b>Traduis cette information par un nombre relatif</b>, écrit avec son signe (ex. : +200 ou -52).</p>`,
        opts(L_TRADUIRE));
    payload._meta.params = { relatif: ctx.relatif };
    return payload;
};

const genTraduireQcm = () => {
    const [ctx] = tirerContextes(1);
    const v = Math.abs(ctx.relatif);
    const correct = signe(ctx.relatif * 10);
    const autreV = v >= 20 ? Math.round(v / 10) : v * 10; // erreur de grandeur (rang)
    const distracteurs = [signe(-ctx.relatif * 10), signe(Math.sign(ctx.relatif) * autreV * 10), signe(-Math.sign(ctx.relatif) * autreV * 10)];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>${ctx.phrase} <b>Quel nombre relatif traduit cette information ?</b></p>`,
        opts(L_TRADUIRE));
    payload._meta.params = { relatif: ctx.relatif };
    return payload;
};

// ═════════ Carte 2 — L'ascenseur (Ex.1 adapté) ═════════

const IMMEUBLES = [
    'Un immeuble d\'habitation', 'La résidence des Tilleuls', 'Un hôtel du centre-ville',
    'Un immeuble de bureaux', 'La tour du quartier neuf', 'Une clinique',
    'Un grand magasin', 'Un immeuble récent',
];

const tirerImmeuble = () => {
    const sousSols = randInt(1, 3);
    const etages = randInt(3, Math.min(7, 8 - sousSols)); // 8-9 boutons max
    return { sousSols, etages, lieu: randomChoice(IMMEUBLES) };
};

const genAscenseurOrdonner = () => {
    const { sousSols, etages, lieu } = tirerImmeuble();
    const items = [];
    for (let i = -sousSols; i <= etages; i++) items.push(String(i));
    const payload = makeOrdonner(items,
        `<p>${lieu} comporte ${sousSols} niveau${sousSols > 1 ? 'x' : ''} de parking en sous-sol, `
        + `un rez-de-chaussée et ${etages} étages. `
        + '<b>Remets les boutons de l\'ascenseur dans l\'ordre, du niveau le plus bas au niveau le plus haut.</b></p>',
        opts(L_ASCENSEUR));
    payload._meta.params = { sousSols, etages };
    return payload;
};

const genAscenseurDrag = () => {
    const { sousSols, etages, lieu } = tirerImmeuble();
    let e = randInt(2, etages);
    if (e === sousSols) e = e === etages ? e - 1 : e + 1; // e ≠ s : sinon les distracteurs collisionnent tous
    const phrase = `Le parking niveau ${sousSols} correspond au bouton {{-${sousSols}}} ; `
        + `le rez-de-chaussée au bouton {{0}} ; le ${e}e étage au bouton {{${e}}}.`;
    const candidats = [String(sousSols), `-${e}`, String(etages + 1), `-${etages + 1}`];
    const payload = makeTexteTrou(phrase,
        `<p>${lieu} comporte ${sousSols} niveau${sousSols > 1 ? 'x' : ''} de parking en sous-sol, `
        + `un rez-de-chaussée et ${etages} étages. <b>Complète le panneau de l'ascenseur.</b></p>`,
        { ...opts(L_ASCENSEUR), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { sousSols, etages, e };
    return payload;
};

// ═════════ Carte 3 — Variations de température (Créé n°12) ═════════
// Valeurs en DIXIÈMES ; la variation est lue sur l'échelle, jamais calculée
// par une « règle des signes ».

const tirerVariation = () => {
    const type = randomChoice(['baisse', 'hausse', 'passage', 'meme_signe']);
    if (type === 'baisse') {
        const v = randomChoice([true, false]) ? randInt(2, 9) * 10 : randInt(15, 85);
        return { type, texte: `La température a baissé de ${dix(v)} °C.`, variation: -v };
    }
    if (type === 'hausse') {
        const v = randomChoice([true, false]) ? randInt(2, 9) * 10 : randInt(15, 85);
        return { type, texte: `La température a augmenté de ${dix(v)} °C.`, variation: v };
    }
    if (type === 'passage') {
        const a = randInt(1, 8) * 10;
        const b = -randInt(1, 9) * 10;
        return { type, texte: `La température est passée de ${dix(a)} °C à ${dix(b)} °C.`, variation: b - a };
    }
    let a = -randInt(1, 9) * 10;
    let b = -randInt(1, 9) * 10;
    for (let essai = 0; essai < 20 && b === a; essai++) b = -randInt(1, 9) * 10;
    if (b === a) b = a + 10;
    return { type, texte: `Il faisait ${dix(a)} °C, il fait maintenant ${dix(b)} °C.`, variation: b - a };
};

const genVariationsDrag = () => {
    const v = tirerVariation();
    const phrase = `${v.texte} Cette variation se traduit par le nombre relatif {{${signe(v.variation)}}}.`;
    const candidats = [signe(-v.variation), signe(v.variation + 10), signe(v.variation - 10)];
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète avec le nombre relatif qui traduit la variation de température.</b></p>',
        { ...opts(L_VARIATIONS), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { variation: v.variation, type: v.type };
    return payload;
};

const genVariationsQcm = () => {
    const v = tirerVariation();
    const correct = signe(v.variation);
    const candidats = [signe(-v.variation), signe(v.variation + 10), signe(v.variation - 10), signe(-v.variation + 10)];
    const distracteurs = [];
    candidats.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>${v.texte} <b>Quel nombre relatif traduit cette variation de température ?</b></p>`,
        opts(L_VARIATIONS));
    payload._meta.params = { variation: v.variation, type: v.type };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    traduire_relier: genTraduireRelier,
    traduire_texte: genTraduireSaisie,
    traduire_qcm_unique: genTraduireQcm,
    ascenseur_ordonner: genAscenseurOrdonner,
    ascenseur_texte_trou_drag: genAscenseurDrag,
    variations_texte_trou_drag: genVariationsDrag,
    variations_qcm_unique: genVariationsQcm,
};

const FORMAT_LABELS = {
    relier: 'Relier', texte: 'Saisie', qcm_unique: 'QCM',
    ordonner: 'Ordonner', texte_trou_drag: 'Drag',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'traduire', label: L_TRADUIRE, formatsList: buildFormats('traduire') },
    { id: 'ascenseur', label: L_ASCENSEUR, formatsList: buildFormats('ascenseur') },
    { id: 'variations', label: L_VARIATIONS, formatsList: buildFormats('variations') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
